import { useContext, useEffect, useState } from 'react';
import axios from '../config/axios';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import { UserContext } from '../context/userContext';
import { CartContext } from '../context/cartContext';
import {
  CartDetailType,
  Product,
  ProductListProps,
} from '../interface/interface';
import { calculateOutOfStockEnum } from '../utils/outOfStockStatus';
import { useQuery } from '@tanstack/react-query';
import { getNonOrderableProducts } from '../service/getNonOrderableProducts';
import { PatientOrderTab } from './patientOrderTab';
import { WishListContext } from '../context/wishListContext';
import { ShowNonScriptView } from '../components/ShowNonScriptViewForNoPrescriptions';

export const ProductList = ({ home }: ProductListProps) => {
  const { user } = useAuth();
  const { cart, addCart, removeCart } = useContext(CartContext);
  const { userDetail, setLoadingEnable } = useContext(UserContext);
  const [loading, setLoading] = useState(true);

  const [products, setProducts] = useState<Array<any>>([]);
  const [accessories, setAccessories] = useState<Array<any>>([]);
  const { wishListData } = useContext(WishListContext);

  const frameProductObject = (
    products: Array<Product>,
    cart?: CartDetailType,
  ) => {
    const quantityCheckedProducts = products.map((product: any) => {
      const orderedQuantity =
        cart?.line_items?.find(
          (item: any) => `${item?.product_id}` === `${product?.product_code}`,
        )?.quantity ?? 0;
      if (product.substituteProduct) {
        const subProduct = product.substituteProduct;
        const subOrderedQuantity =
          cart?.line_items?.find(
            (item: any) =>
              `${item?.product_id}` === `${subProduct?.product_code}`,
          )?.quantity ?? 0;
        return {
          ...product,
          substituteProduct: {
            ...subProduct,
            productMaxQty: product.productMaxQty,
            orderedQuantity: subOrderedQuantity,
            outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
          },
          orderedQuantity,
          outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
        };
      }

      return {
        ...product,
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    });
    return quantityCheckedProducts;
  };

  const callProducts = async (cart?: CartDetailType) => {
    try {
      const response = await axios('/api/v1/products', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      const products = response?.data;

      const quantityCheckedProducts = frameProductObject(products, cart);

      setProducts(quantityCheckedProducts);
      return true;
    } catch (error) {
      return false;
    }
  };

  const callTestKitAccessories = async (cart?: CartDetailType) => {
    try {
      const response = await axios('/api/v1/testkit/accessories', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      const products = response?.data;

      const quantityCheckedProducts = frameProductObject(products, cart);

      setAccessories(quantityCheckedProducts);
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await callProducts(cart);
      await callTestKitAccessories(cart);
      setLoading(false);
    };

    fetchData();
  }, [cart]);

  useEffect(() => {
    if (!loading) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [loading]);

  const { isLoading, data: nonOrderable } = useQuery({
    queryKey: ['non-orderable-products', user?.access_token],
    queryFn: ({ queryKey }) => getNonOrderableProducts(queryKey[1]!),
  });

  if (loading || isLoading) {
    return <Loading />;
  }

  if (!products?.length && !nonOrderable?.length) {
    return <ShowNonScriptView />;
  }

  return (
    <div>
      <p className="lead mb-5 hidden">
        {`The list of products for user, ${userDetail.name}, based on your prescriptions and Storefront (Shopify) products.`}
      </p>
      {!loading ? (
        <PatientOrderTab
          products={[...products, ...accessories]}
          addProductToCart={addCart}
          removeProductFromCart={removeCart}
          nonOrderable={nonOrderable}
          isHome={home}
          wishListProducts={wishListData}
        />
      ) : null}
    </div>
  );
};

export default ProductList;
